<script setup lang="ts" generic="T">
import type {
	AcceptableValue,
	ClickedHighlightedItem,
	DefaultFilterSelectProps
} from '.';

import { cn } from '@/utils';
import { getBoldMatchCaracteres } from '~~/utils';

import {
	Command,
	CommandInput,
	CommandItem,
	CommandList
} from '~/components/ui/command';

import { separatorHighlightedItemsVariant } from '.';
import messages from './DefaultFilterSelect.locale.json';

interface ItemWithUniqueKey {
	uniqueKey?: string;
}

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<DefaultFilterSelectProps<T>>(), {
	title: '',
	items: () => [],
	highlightedItems: () => [],
	getItemLabel: (item: T) => `${item}`,
	filterFunction: (item: T[], term: string) => item,
	objectValue: false,
	valueProp: '',
	hideWhenDetached: true,
	isLoading: false
});

const modelValue = defineModel<T[]>({ required: true, default: () => [] });
const searchTerm = shallowRef<string>('');
const isDropdownOpened = shallowRef<boolean>(false);
const commandEl = ref<HTMLElement | null>(null);

watch(modelValue, () => (isDropdownOpened.value = false));

const inputPlaceholder = computed<string>(() =>
	t('placeholder', { title: props.title.toLowerCase() })
);

const filteredHighlightedItems = computed(() => {
	return props.highlightedItems.filter(item => {
		const itemValue = getItemValue(item) as T;

		// Check if the item is already selected using our centralized comparison
		const isAlreadySelected = modelValue.value.some(model =>
			itemsAreEqual(itemValue, model)
		);

		return !isAlreadySelected;
	});
});

const hasHilightedItems = computed<boolean>(
	() => !!filteredHighlightedItems.value.length
);

function clear() {
	searchTerm.value = '';
}

function getItemValue(item: T): AcceptableValue {
	if (typeof item === 'object' && props.objectValue && props.valueProp) {
		return (item?.[props.valueProp as keyof typeof item] ||
			item) as AcceptableValue;
	}

	return item as AcceptableValue;
}

const emit = defineEmits<{
	(event: 'click-highlight-item', item: ClickedHighlightedItem<T>): void;
}>();

function itemsAreEqual(itemA: T, itemB: T): boolean {
	const keyA = (itemA as ItemWithUniqueKey)?.uniqueKey;
	if (keyA !== undefined) {
		const keyB = (itemB as ItemWithUniqueKey)?.uniqueKey;
		return keyA === keyB;
	}
	return itemA === itemB;
}

function updateModel(value: T) {
	const isSelected = modelValue.value.some(item => itemsAreEqual(item, value));
	if (isSelected) {
		modelValue.value = modelValue.value.filter(v => !itemsAreEqual(v, value));
	} else {
		modelValue.value = [...modelValue.value, value];
	}
}

function updateModelHighlightItem(value: T, index: number) {
	updateModel(value);
	emit('click-highlight-item', { item: value, index });
}

function isItemSelected(item: T): boolean {
	return modelValue.value.some(selectedItem =>
		itemsAreEqual(selectedItem, item)
	);
}

onClickOutside(commandEl, () => {
	isDropdownOpened.value = false;
});

defineExpose({ searchTerm });
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<div class="w-full space-y-5">
		<p
			class="font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] text-primary"
		>
			{{ title }}
		</p>

		<div ref="commandEl" class="relative size-auto h-[50px]">
			<Command
				v-model:model-value="modelValue"
				v-model:open="isDropdownOpened"
				:filter-function="filterFunction as any"
				class="absolute z-50"
				:class="[cn('h-auto w-full bg-white', props.class), {}]"
				multiple
			>
				<CommandInput
					v-model.trim="searchTerm"
					:placeholder="inputPlaceholder"
					:wrapper-classes="[
						'border border-white-dark-4 hover:border-primary/40 hover:focus-within:border-primary focus-within:border-primary'
					]"
					class="h-[50px] px-4 py-2 text-primary placeholder:text-sm"
					@focus="isDropdownOpened = true"
				>
					<template #prefix>
						<div class="grid h-full w-min place-items-center pl-3">
							<IconMaterialSymbolsLightSearch class="size-7 text-primary" />
						</div>
					</template>
					<template #suffix>
						<div
							v-show="searchTerm"
							class="mr-3 flex h-full w-7 items-center justify-end"
						>
							<IconMaterialSymbolsLightCancel
								class="size-5 cursor-pointer text-white-dark-3"
								@click="clear"
							/>
						</div>
					</template>
				</CommandInput>

				<CommandList
					class="p-0"
					:class="{ 'border border-white-dark-4': items.length || isLoading }"
				>
					<!-- Loading State -->
					<div v-if="isLoading" class="flex flex-col gap-2 p-4">
						<div
							v-for="i in 3"
							:key="`loading-skeleton-${i}`"
							class="flex animate-pulse flex-col gap-1"
						>
							<div class="h-4 w-3/4 rounded bg-white-dark-3" />
							<div class="h-3 w-1/2 rounded bg-white-dark-4" />
						</div>
					</div>

					<!-- Items List -->
					<template v-else>
						<CommandItem
							v-for="item in items"
							:key="`default-filter-select-${title}-${(item as any)?.uniqueKey || getItemLabel(item)}`"
							:value="getItemValue(item)"
							class="flex items-center justify-between px-4 py-3 text-base font-normal leading-normal text-primary data-highlighted:bg-white-dark-2"
							:class="{
								'bg-white-dark-2': isItemSelected(item)
							}"
						>
							<slot name="item" :item="item" :search-term="searchTerm">
								<p
									v-html="
										getBoldMatchCaracteres(getItemLabel(item), searchTerm)
									"
								/>
							</slot>
						</CommandItem>
					</template>
				</CommandList>
			</Command>
		</div>

		<div v-show="modelValue.length" class="flex flex-wrap gap-2 pt-2">
			<Badge
				v-for="item in modelValue"
				:key="`default-filter-select-badge-${(item as any)?.uniqueKey || getItemLabel(item)}`"
				variant="primary"
				class="w-min cursor-pointer gap-2 whitespace-nowrap px-2 py-[6px] text-sm font-normal leading-normal"
				@click="updateModel(item)"
			>
				<slot name="selected-item-badge" :item="item" :search-term="searchTerm">
					{{ getItemLabel(item) }}
				</slot>
				<IconMaterialSymbolsLightClose class="size-5" />
			</Badge>
		</div>

		<Separator
			v-if="hasHilightedItems && modelValue.length"
			:class="
				cn(separatorHighlightedItemsVariant({ variant: separatorVariant }))
			"
		/>

		<div v-if="hasHilightedItems">
			<p class="text-xs font-normal leading-normal text-primary-light-1">
				{{ t('highlightTitle', { title }) }}
			</p>
			<div class="flex flex-wrap gap-2 pt-2">
				<Badge
					v-for="(item, index) in filteredHighlightedItems"
					:key="`default-filter-select-highlighted-badge-${(item as ItemWithUniqueKey)?.uniqueKey || getItemLabel(item)}`"
					variant="tertiary"
					class="w-min cursor-pointer gap-2 whitespace-nowrap px-2 py-[6px] text-sm font-normal leading-normal"
					@click="updateModelHighlightItem(item, index)"
				>
					<IconMaterialSymbolsLightAdd class="size-5 text-primary" />
					<slot
						name="highlighted-item-badge"
						:item="item"
						:search-term="searchTerm"
					>
						{{ getItemLabel(item) }}
					</slot>
				</Badge>
			</div>
		</div>
	</div>
</template>
